import React, { useState } from "react";
import { Box } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { taskEditorTasksByTaskIdAtom } from "recoil/taskEditorStoreAtom";
import { theme } from "styles/theme";
import { taskEditorScrollbarSx } from "../utils/taskEditorScrollbarSx";
import type { AssistantStep } from "./types";

const LIBRARY_PREFIX = "/library/";
const LIBRARY_INDEX_PATH = "/library/index.json";

// /library/<task_id>.json 도구 인자를 불투명 id 대신 활동지 제목으로 보여준다.
// 카탈로그에 없으면 null → 호출부가 기존 파일명 요약으로 폴백.
function libraryFileLabel(
  filePath: string,
  tasksByTaskId: Record<string, { title?: string }>
): string | null {
  if (!filePath.startsWith(LIBRARY_PREFIX) || !filePath.endsWith(".json")) {
    return null;
  }
  if (filePath === LIBRARY_INDEX_PATH) return "활동지 목록";
  const taskId = filePath.slice(LIBRARY_PREFIX.length, -".json".length);
  const title = tasksByTaskId[taskId]?.title;
  return title ? `참고: ${title}` : null;
}

function summarizeToolArgs(
  name: string,
  args: Record<string, unknown>
): string {
  // task(서브에이전트 위임)는 어떤 에이전트인지 subagent_type 을 요약으로 보인다.
  if (name === "task") {
    return typeof args.subagent_type === "string" ? args.subagent_type : "";
  }
  if (typeof args.file_path === "string") {
    const parts = args.file_path.split("/");
    return parts[parts.length - 1] || args.file_path;
  }
  if (typeof args.path === "string") return args.path;
  return Object.keys(args).slice(0, 2).join(", ") || "";
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// =========================
// ToolCallRow — line status with expandable result
// =========================

export function ToolCallRow({
  step,
  active = true,
}: {
  step: Extract<AssistantStep, { kind: "tool" }>;
  // turn 이 실제로 streaming 중일 때만 스피너를 돌린다. turn 이 종료(에러/타임아웃/
  // 완료)됐는데 tool_result 가 끝내 안 온 step 은 spinning 대신 정적 마커로 멈춘다.
  active?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // running 이지만 turn 이 끝났으면 "미완료(중단)" — 스피너를 멈춘다.
  const spinning = step.status === "running" && active;
  const stalled = step.status === "running" && !active;
  const tasksByTaskId = useRecoilValue(taskEditorTasksByTaskIdAtom);
  const filePath =
    typeof step.args.file_path === "string" ? step.args.file_path : null;
  const libLabel = filePath ? libraryFileLabel(filePath, tasksByTaskId) : null;
  const argsSummary = libLabel ?? summarizeToolArgs(step.name, step.args);
  const duration =
    step.finishedAt && step.startedAt
      ? formatDuration(step.finishedAt - step.startedAt)
      : null;
  const hasResult = !!step.result;

  return (
    <Box
      onClick={() => hasResult && setExpanded((v) => !v)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        px: "8px",
        py: "5px",
        borderRadius: "6px",
        cursor: hasResult ? "pointer" : "default",
        transition: "background-color 0.15s",
        "&:hover": hasResult
          ? { backgroundColor: theme.vars.palette.Background.Level2 }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: spinning
              ? theme.vars.palette.primary.solid
              : stalled
                ? "#b91c1c"
                : "#15803d",
            flexShrink: 0,
          }}
        >
          {spinning ? (
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: `1.5px solid ${theme.vars.palette.Background.Level3 ?? "#ddd"}`,
                borderTopColor: theme.vars.palette.primary.solid,
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": {
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ) : stalled ? (
            // 미완료(실패/중단) — tool_result 없이 turn 이 끝남. 빨간 ✕ 로 표시.
            <Box
              component="span"
              sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 700 }}
            >
              ✕
            </Box>
          ) : (
            <Box
              component="span"
              sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 700 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 600,
          }}
        >
          {step.name}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {argsSummary}
        </Box>
        {duration && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {duration}
          </Box>
        )}
        {hasResult && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {expanded ? "▾" : "▸"}
          </Box>
        )}
      </Box>
      {expanded && step.result && (
        <Box
          sx={{
            mt: "6px",
            ml: "20px",
            p: "8px 10px",
            borderRadius: "4px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "11px",
            lineHeight: 1.5,
            color: theme.vars.palette.Content.Medium,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "200px",
            overflow: "auto",
            ...taskEditorScrollbarSx,
          }}
        >
          {step.result}
        </Box>
      )}
    </Box>
  );
}
